import clsx from 'clsx';
import { useValue } from '../hooks/useValue';
import { useInternalStatus } from '../hooks/useInternalStatus';
import { FormControl, type FormControlProps } from './FormControl';
import { Icon, type IconicTypes } from '@clubmed/trident-icons';
import { type SelectHTMLAttributes, useId } from 'react';

export interface SelectProps<Value>
  extends FormControlProps<Value, SelectHTMLAttributes<HTMLSelectElement>> {
  description?: string;
  iconType?: IconicTypes;
  errorMessage?: string;
  dataTestId?: string;
}

export const Select = <Value = string,>(props: SelectProps<Value>) => {
  const internalId = useId();

  const {
    id = internalId,
    name = id,
    label,
    value: initialValue,
    description,
    validationStatus = 'default',
    iconType,
    errorMessage,
    disabled = false,
    required = false,
    hideRequiredStar,
    className,
    dataTestId = id,
    onChange,
    children,
    ...rest
  } = props;

  const { value, setValue } = useValue<Value>({ name, initialValue, onChange });
  const internalStatus = useInternalStatus({
    isDisabled: disabled,
    validationStatus,
  });

  return (
    <FormControl
      id={id}
      className={className}
      label={label}
      description={description}
      dataName="TextField"
      dataTestId={dataTestId}
      disabled={disabled}
      required={required}
      hideRequiredStar={hideRequiredStar}
      validationStatus={validationStatus}
      errorMessage={errorMessage}
    >
      <div
        className={clsx('relative rounded-pill z-0', {
          'bg-white': internalStatus !== 'disabled',
          'bg-pearl': internalStatus === 'disabled',
        })}
      >
        <select
          {...rest}
          id={id}
          name={name}
          disabled={disabled}
          required={required}
          value={value as any}
          onChange={(e) => setValue(e.target.value as Value)}
          className={clsx(
            'text-b3 rounded-pill w-full border px-20 py-12 font-normal outline-none appearance-none bg-transparent',
            {
              'border-lightGrey focus:border-black active:border-black pe-[52px]':
                internalStatus === 'default',
              'pe-[84px]': internalStatus === 'error' || internalStatus === 'success',
              'text-black': internalStatus !== 'disabled',
              'text-grey': internalStatus === 'disabled',
              'border-red': internalStatus === 'error',
              'border-green': internalStatus === 'success',
            },
          )}
          aria-label={name}
        >
          {children}
        </select>

        <div
          className={clsx(
            'pointer-events-none absolute inset-0 flex items-center justify-between px-20 py-12 -z-1',
            {
              'text-grey': internalStatus === 'disabled',
              'text-red': internalStatus === 'error',
              'text-green': internalStatus === 'success',
            },
          )}
        >
          <span className="ms-auto flex gap-x-8">
            {internalStatus === 'error' && (
              <Icon name="CrossDefault" width="24px" type={iconType} />
            )}

            {internalStatus === 'success' && (
              <Icon name="CheckDefault" width="24px" type={iconType} />
            )}

            <button className="pointer-events-auto inline-flex">
              <Icon name="ArrowDefaultDown" type="svg" width="24px" color="black" />
            </button>
          </span>
        </div>
      </div>
    </FormControl>
  );
};
